import { STALE_TIME } from 'business/constants';
import { useIsSupportUser } from 'hooks/user';
import i18n, { getTranslationsPaths } from 'i18n';
import {
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import { FINANCE_SOFTWARE_SWITCH } from 'routes/Support/components/ClientSupport/components/ClientConfiguration/ModuleForm/helpers/definitions';
import {
    HttpContentWithoutData,
    HttpError,
    RQAPICall,
    RQAPICallWithData,
} from 'store/APIClient';
import { useDispatch, useSelector } from 'store/hooks';

const OVERRIDABLE_DEMO_FINANCE_SOFTWARE_NAMESPACES = [
    'blocks_manty',
    'blocks_manty_short',
];

/**
 * Fetch the current finance software demo override.
 */
export const useGetCurrentDemoFinanceSoftware = ({
    enabled,
}: Pick<UseQueryOptions, 'enabled'>): UseQueryResult<string, HttpError> => {
    return useQuery<string, HttpError>({
        queryKey: ['currentFinanceSoftware'],
        queryFn: async () => {
            const res = await RQAPICallWithData<{ software: string }>({
                endpoint: 'budget/multi_software/current',
            });
            const { data } = res.content;
            return data.software;
        },
        meta: {
            useGlobalOnError: true,
        },
        staleTime: STALE_TIME,
        enabled,
    });
};

/**
 * Fetch all the available finance software for the demo override.
 */
export const useGetAvailableDemoFinanceSoftwares = () => {
    return useQuery({
        queryKey: ['availableFinanceSoftwares'],

        queryFn: async () => {
            const res = await RQAPICallWithData<{ softwares: string[] }>({
                endpoint: 'budget/multi_software/index',
            });
            const { data } = res.content;
            return data.softwares;
        },

        meta: {
            useGlobalOnError: true,
        },

        staleTime: STALE_TIME,
    });
};

export const useUpdateDemoFinanceSoftware = (): UseMutationResult<
    unknown,
    HttpError,
    string
> => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (softwareName: string) =>
            RQAPICall<HttpContentWithoutData>({
                endpoint: `budget/multi_software/${softwareName}`,
                method: 'POST',
            }),

        onSuccess: (response, softwareName: string) => {
            const translationPaths = getTranslationsPaths(
                ['fr', 'en'],
                OVERRIDABLE_DEMO_FINANCE_SOFTWARE_NAMESPACES,
            );
            translationPaths.forEach((pathData) => {
                fetch(pathData.path).then(() =>
                    // avoids caching issues
                    i18n.reloadResources(pathData.lng, pathData.namespace),
                );
            });
            queryClient.setQueryData(
                ['currentFinanceSoftware'],
                () => softwareName,
            );
            queryClient.invalidateQueries({
                queryKey: ['useGetFiltersValues', 'antenna.name'],
            });
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

export const useHasDemoFinanceSoftwareOverride = (): boolean => {
    const isOverrideFeatureEnabled = useSelector(
        (state) => state.vision.client.extraFeatures,
    ).includes(FINANCE_SOFTWARE_SWITCH);
    const isSupportUser = useIsSupportUser();
    return isOverrideFeatureEnabled && isSupportUser;
};

/**
 * Performs checks to determine if the budget home's finance software override selector should be displayed to
 * current user.
 */
export const useShouldDisplayDemoFinanceSoftwareSelector = (
    canShowFinanceSoftwareSelector: boolean,
): boolean => {
    const canUseOverride = useHasDemoFinanceSoftwareOverride();
    return canShowFinanceSoftwareSelector && canUseOverride;
};
